<template>
    <div>
        <section id="demo" class="demo observer-fade bg-none">
            <div class="container">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-visual left">
                        <div class="card-stack">
                            <div v-for="item in 4" :key="item" class="card grid gap-2">
                                <h2 class="text--sm text--semi--bold text-gray-600">
                                    REVENUE FOR AUGUST 2025
                                </h2>
                                <p class="text--2xl text--bold text--upper text-gray-700">
                                    $22,000
                                </p>
                                <div class="flex align-items center">
                                    <div class="stats__rate mr--5 red bg-none pl--0">
                                        <IconSvg name="down-arrow-alt" fill="#651b37" />
                                        <span class="stats__rate__value">23%</span>
                                    </div>
                                    <p class="text--xs text-gray-500">
                                        down from last month
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="demo-content line">
                        <h2>Revenue Analytics</h2> 
                        <p>
                            <strong>Measure revenue performance</strong>. Get reports on your financial growth as a digital creator.
                        </p>
                        <div class="features-grid mt--15 gap-3">
                            <div v-for="item in automateItems" :key="item" class="feature-item flex align-items-center">
                                <div class="circle">
                                    <IconSvg name="arrow-right" width="12px" height="12px" fill="#228E9D" />
                                </div>
                                <span class="ml--10 text--sm list-check">{{ item }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade pb-3">
            <div class="container">
                <div class="demo-container two-col">
                    <div class="demo-content">
                        <h2>Invoice management</h2>
                        <p>
                            <strong>Get paid faster without friction</strong>. Create and send invoices from projects or milestones.
                        </p>
                        <ul class="demo-features">
                            <li 
                                v-for="(item, index) in ['Custom branding', 'Custom templates (coming soon)', 'Automatic tax & discount calculations', 'Accept payments through gateways like Stripe or Paystack.', 'Real-time payment tracking']"
                                :key="index"
                            >
                                <span class="check-icon">✓</span> 
                                {{ item }}
                            </li>
                        </ul>
                    </div>

                    <div class="demo-visual">
                        <div class="invoice-preview">
                            <div class="invoice-header">
                                <div class="invoice-title">
                                    Invoice #1024
                                </div>
                                <div class="invoice-status">
                                    Paid
                                </div>
                            </div>
                            <div class="invoice-item">
                                <span>Web Design Services</span>
                                <span>$2,500.00</span>
                            </div>
                            <div class="invoice-item">
                                <span>Logo Design</span>
                                <span>$750.00</span>
                            </div>
                            <div class="invoice-item">
                                <span>Consultation (4 hours)</span>
                                <span>$400.00</span>
                            </div>
                            <div class="invoice-total">
                                <span>Total:</span>
                                <span>$3,650.00</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade bg-none pb-3">
            <div class="container">
                <div class="demo-container md">
                    <div class="demo-content mb--15">
                        <h2>Manage invoices without leaving the app</h2> 
                    </div>
                    
                    <div class="demo-container img one-col bg-none p-0">    
                        <div class="grid gap-3 reports-demo-wrap">
                            <div v-for="item in invoiceFeat" :key="item.title" class="reports-demo">
                                <h2>{{ item.title }}</h2>
                                <p>{{ item.desc }}</p>
                            </div>
                        </div>                  
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade">
            <div class="container">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-content line">
                        <h2>Payment support</h2>
                        <p>
                            <strong>Multiple payment options for sending invoices</strong>. Make use of existing payment providers, payment links or simply attach your personal bank account for invoice payment.
                        </p>
                    </div>

                    <div class="demo-visual grid gap-3">
                        <div v-for="(item, index) in paymentProviders" :key="index" class="invoice-preview linked p-3 align-items-center flex">
                            <div class="icon reminders money flex align-items-center mr--10">
                                <span class="text--lg">✅</span>
                            </div>
                            <div class="">
                                <span class="feature-title text--sm">{{ item.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade">
            <div class="container">
                <div class="demo-container two-col split">
                    <div class="demo-container one-col bg-none floating p-0">
                        <div class="tasks-demo">
                            <div class="demo-task-item completed fade-in">
                                <div class="p-3">
                                    <div class="stat-label">
                                        Total Revenue
                                    </div>
                                    <div class="stat-value">
                                        $1,250,000
                                    </div>
                                    <div class="stat-change positive">
                                        <span class="stat-icon">↑</span>+12% increase today
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                
                    <div class="demo-content">
                        <h2>Revenue tracking</h2> 
                        <p>
                            <strong>Capture revenue made from paid projects or invoices</strong>. Track your revenue, spot periods of growth, and know your finances are on the right track.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade bg-none">
            <div class="container">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-content line">
                        <h2>Revenue goals</h2> 
                        <p>
                            <strong>Maximize and measure your revenue potential</strong>. Set attainable revenue targets to get a clearer picture of how to increase your earnings as a creative professional.
                        </p>
                    </div>

                    <div class="img-blur-outer">
                        <div class="img-blur">
                            <img src="@/assets/img/revenue_goal.svg" alt="revenue goals sample image">
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import IconSvg from "../ui/icons/Icon-Svg.vue";

export default {
    name: "InvoicingPage",

    components: { IconSvg },
    
    data() {
        return {
            invoiceFeat: [
                { icon: "⚡", title: "Instant Invoice Creation", desc: "Generate professional invoices with templates, automatic calculations, and your custom brand logo." },
                { icon: "🤖", title: "Automated Follow-ups", desc: "Automated payment reminders with the options for autogenerated or custom follow-up messages." },
                { icon: "🔄", title: "Recurring Billing", desc: "Set up automatic recurring invoices for retainer clients." },
            ],
            automateItems: ["Know your highest paying clients", "Get data on revenue generating projects", "Understand your financial performance as a creator"],
            observer: null,
            paymentProviders: [
                { icon: "➕ ", name: "Use Stripe" },
                { icon: "📱", name: "Use Paystack" },
                { icon: "📱", name: "Payment links" },
                { icon: "📱", name: "Personal bank account" },
            ],
        };
    },

    async mounted() {
        this.initPageIntersectionObserver();
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },

    methods: {
        initPageIntersectionObserver() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: "0px 0px -50px 0px",
            });

            // Observe all elements with observer-fade class
            document.querySelectorAll(".observer-fade").forEach(el => {                
                this.observer.observe(el);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    @media (max-width: 768px) {
        .features-grid {
            grid-template-columns: 1fr;
        }
        .container {
            padding: 0 1rem;
        }
    }

    .card {
        position: absolute;
        width: 100%;
        height: 100%;
        background: white;
        border: 3px solid #333;
        border-radius: 12px;
        padding: 32px;
        box-sizing: border-box;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;

        &:nth-child(1) {
            top: 0;
            left: 0;
            z-index: 4;
        }
    
        &:nth-child(2) {
            top: -8px;
            left: 12px;
            z-index: 3;
            opacity: 0.9;
        }
    
        &:nth-child(3) {
            top: -16px;
            left: 24px;
            z-index: 2;
            opacity: 0.8;
        }
    
        &:nth-child(4) {
            top: -24px;
            left: 36px;
            z-index: 1;
            opacity: 0.7;
        }

        h2 {
            margin: 0;
            letter-spacing: 1px;
        }
    
        p {
            margin: 0;
            font-size: 16px;
            line-height: 1.5;
            color: #333;
        }

        &-stack {
            position: relative;
            width: 320px;
            height: 240px;
            height: 150px;

            &:hover .card:nth-child(1) {
                transform: translateY(-10px);
                box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
            }
        
            &:hover .card:nth-child(2) {
                transform: translateY(-6px);
            }
        
            &:hover .card:nth-child(3) {
                transform: translateY(-3px);
            }
        
            &:hover .card:nth-child(4) {
                transform: translateY(-1px);
            }
        }

        @media (max-width: 480px) {
            &:nth-child(2) {
                left: 9px;
            }
        
            &:nth-child(3) {
                left: 16px;
            }
        
            &:nth-child(4) {
                left: 23px;
            }

            &-stack {
                position: relative;
                width: 300px;
                height: 200px;
            }

            padding: 24px;

            h2 {
                font-size: 20px;
                margin-bottom: 16px;
            }
            
            p {
                font-size: 14px;
            }
        }
    }

    @media (max-width: 480px) {
        .demo-visual {
            padding: 2rem 1rem 1rem;
        }
    }

    .demo-container {
        &.full {
            grid-template-columns: 1fr;
            max-width: 60%;
            margin: auto;
        }

        &.md {
            grid-template-columns: 1fr;
            max-width: 80%;
            margin: auto;
            border-top: 1px solid #e0e5ec;
            border-bottom: 1px solid #e0e5ec;
            padding: 3rem 0;
        }

        &.img {
            margin-top: 4rem;
        }
    }

    .reports-demo-wrap {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 2rem;
    }

    .reports-demo {
        color: #1e293b;

        h2 {
            font-size: 1.25rem;
            font-weight: 500;
        }

        p {
            font-size: .975rem;
            margin-top: 0.75rem;
            color: #4A5568;
            line-height: 23px;
            color: #64748b;
        }
    }

    .demo-visual {
        padding-top: 3rem;
    }

    .stat-label {
        font-size: 12px;
        color: #999;
        font-weight: 500;
        margin-bottom: 8px;
        text-transform: uppercase;
    }

    .stat-value {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #333;
    }

    .stat-change {
        font-size: 12px;
        font-weight: 500;
    }

    .stat-change.positive {
        color: #27ae60;
    }

    .stat-icon {
        display: inline-block;
        width: 16px;
        height: 16px;
        vertical-align: middle;
    }
</style>